import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { ReportQuarter } from "@/apis/workbook/types";

/**
 * 지금 비교 중인 대상 — 워크북 id·보고 기간과 IXD 보고서 id.
 *
 * 둘 다 **화면이 이미 확정한 값**이다(어느 워크북으로 preview/plan 을 받고 있는지).
 * 비교 본문 안의 컴포넌트가 URL 에서 다시 유도하면 화면 상태와 어긋나는 순간이
 * 생기고(유형 전환 직후처럼 URL 이 state 를 한 박자 늦게 따라오는 구간),
 * 저장은 되지만 캐시를 엉뚱한 키에 써서 표가 갱신되지 않는다(WXBRL-2658).
 * 그래서 진실 공급원은 화면 하나로 두고 여기로 내린다.
 *
 * 비교 행은 CompareTableRows → 행 → 섹션으로 여러 홉을 거치고 그 사이
 * 컴포넌트들은 이 값과 무관하므로 prop 으로 흘리지 않고 context 로 준다
 * (`WorkbookCellNoticeProvider` 와 같은 이유).
 */
interface CompareTarget {
  /** 비교 중인 DSD 워크북 — 아직 안 골랐으면 빈 문자열 */
  workbookId: string;
  /** 비교 중인 워크북의 보고 기간 — 모르거나 안 골랐으면 null */
  reportQuarter: ReportQuarter | null;
  reportId: string;
}

const CompareTargetContext = createContext<CompareTarget>({
  workbookId: "",
  reportQuarter: null,
  reportId: "",
});

export const CompareTargetProvider = ({
  workbookId,
  reportQuarter,
  reportId,
  children,
}: CompareTarget & { children: ReactNode }) => {
  const value = useMemo(
    () => ({ workbookId, reportQuarter, reportId }),
    [workbookId, reportQuarter, reportId],
  );
  return (
    <CompareTargetContext.Provider value={value}>
      {children}
    </CompareTargetContext.Provider>
  );
};

export const useCompareTarget = () => useContext(CompareTargetContext);
